/**
 * Searchable country-code picker.
 *
 * Usage:
 *   mountCountrySelect(triggerEl, {
 *     value: 'UA',
 *     onChange: (code) => { ... },
 *   });
 *
 * The trigger element is what the user clicks to open the popup. The popup is
 * appended to <body> and positioned relative to the trigger; this avoids
 * clipping when the trigger sits inside an overflow:hidden ancestor.
 *
 * Only one popup is open at a time. Outside-click, Escape, or scroll closes it.
 */

import { COUNTRIES, findByCode, filterCountries, flagFor, DEFAULT_COUNTRY } from '../../lib/countries.js';
import { escapeHtml } from '../../lib/format.js';

let openPopup = null;

function closeOpenPopup() {
  if (!openPopup) return;
  openPopup.el.remove();
  document.removeEventListener('click', openPopup.onDocClick, true);
  document.removeEventListener('keydown', openPopup.onKey, true);
  window.removeEventListener('scroll', openPopup.onScroll, true);
  window.removeEventListener('resize', openPopup.onResize);
  openPopup = null;
}

function renderTrigger(trigger, code) {
  const c = findByCode(code);
  trigger.innerHTML = `
    <span class="cs-flag" aria-hidden="true">${flagFor(c.code)}</span>
    <span class="cs-dial">+${escapeHtml(c.dial)}</span>
    <span class="cs-chev" aria-hidden="true">▾</span>
  `;
  trigger.setAttribute('data-country', c.code);
  trigger.setAttribute('aria-label', `${c.name} (+${c.dial})`);
}

function buildList(query, currentCode) {
  const items = filterCountries(query);
  if (items.length === 0) {
    return `<li class="cs-empty">Не знайдено</li>`;
  }
  return items.map((c) => `
    <li class="cs-item${c.code === currentCode ? ' is-selected' : ''}" data-code="${c.code}" role="option" tabindex="-1">
      <span class="cs-flag" aria-hidden="true">${flagFor(c.code)}</span>
      <span class="cs-name">${escapeHtml(c.name)}</span>
      <span class="cs-dial">+${escapeHtml(c.dial)}</span>
    </li>
  `).join('');
}

function positionPopup(popup, trigger) {
  const rect = trigger.getBoundingClientRect();
  const popupHeight = 340; // matches max-height in CSS
  const spaceBelow = window.innerHeight - rect.bottom;
  const placeAbove = spaceBelow < popupHeight + 16 && rect.top > popupHeight + 16;

  popup.style.left = `${rect.left + window.scrollX}px`;
  popup.style.minWidth = `${Math.max(rect.width, 320)}px`;
  if (placeAbove) {
    popup.style.top = `${rect.top + window.scrollY - popupHeight - 6}px`;
  } else {
    popup.style.top = `${rect.bottom + window.scrollY + 6}px`;
  }
}

function openFor(trigger, opts) {
  closeOpenPopup();

  const currentCode = (opts.getValue?.() || DEFAULT_COUNTRY).toUpperCase();

  const popup = document.createElement('div');
  popup.className = 'cs-popup';
  popup.setAttribute('role', 'listbox');
  popup.innerHTML = `
    <div class="cs-search-wrap">
      <input class="cs-search" type="text" placeholder="Пошук країни або коду…" autocomplete="off" />
    </div>
    <ul class="cs-list">${buildList('', currentCode)}</ul>
  `;
  document.body.appendChild(popup);
  positionPopup(popup, trigger);

  const search = popup.querySelector('.cs-search');
  const list = popup.querySelector('.cs-list');

  search.focus({ preventScroll: true });

  // Scroll the current selection into view (if no query).
  const sel = list.querySelector('.cs-item.is-selected');
  if (sel) sel.scrollIntoView({ block: 'nearest' });

  const refreshList = () => {
    list.innerHTML = buildList(search.value, currentCode);
  };
  search.addEventListener('input', refreshList);

  // Click on a list item.
  list.addEventListener('click', (e) => {
    const li = e.target.closest('.cs-item');
    if (!li) return;
    const code = li.dataset.code;
    opts.onChange?.(code);
    renderTrigger(trigger, code);
    closeOpenPopup();
  });

  // Keyboard navigation: arrows + Enter.
  let activeIdx = -1;
  const setActive = (idx) => {
    const items = list.querySelectorAll('.cs-item');
    if (items.length === 0) return;
    activeIdx = Math.max(0, Math.min(idx, items.length - 1));
    items.forEach((el, i) => el.classList.toggle('is-active', i === activeIdx));
    items[activeIdx].scrollIntoView({ block: 'nearest' });
  };

  const onKey = (e) => {
    if (e.key === 'Escape') { e.preventDefault(); closeOpenPopup(); return; }
    if (e.key === 'ArrowDown') { e.preventDefault(); setActive(activeIdx + 1); return; }
    if (e.key === 'ArrowUp')   { e.preventDefault(); setActive(activeIdx - 1); return; }
    if (e.key === 'Enter')     {
      e.preventDefault();
      const items = list.querySelectorAll('.cs-item');
      const target = items[activeIdx] || items[0];
      if (target) {
        opts.onChange?.(target.dataset.code);
        renderTrigger(trigger, target.dataset.code);
        closeOpenPopup();
      }
      return;
    }
  };

  const onDocClick = (e) => {
    if (popup.contains(e.target) || trigger.contains(e.target)) return;
    closeOpenPopup();
  };
  const onScroll = (e) => {
    // Reposition on scroll within the page, but close if focus moved far away.
    if (popup.contains(e.target)) return;
    positionPopup(popup, trigger);
  };
  const onResize = () => positionPopup(popup, trigger);

  document.addEventListener('click', onDocClick, true);
  document.addEventListener('keydown', onKey, true);
  window.addEventListener('scroll', onScroll, true);
  window.addEventListener('resize', onResize);

  openPopup = { el: popup, onDocClick, onKey, onScroll, onResize };
}

/**
 * Mount a country picker onto an existing trigger element.
 * Returns a cleanup function that detaches listeners (the trigger itself
 * stays in the DOM — caller owns it).
 *
 * @param {HTMLElement} trigger
 * @param {{ value: string, onChange: (code:string)=>void }} opts
 */
export function mountCountrySelect(trigger, opts) {
  const code = (opts.value || DEFAULT_COUNTRY).toUpperCase();
  renderTrigger(trigger, code);
  trigger.classList.add('cs-trigger');
  trigger.setAttribute('role', 'combobox');
  trigger.setAttribute('aria-haspopup', 'listbox');
  trigger.tabIndex = 0;

  const onClick = (e) => {
    e.preventDefault();
    if (openPopup && openPopup.el?.previousAnchor === trigger) {
      closeOpenPopup();
    } else {
      openFor(trigger, {
        getValue: () => trigger.dataset.country || DEFAULT_COUNTRY,
        onChange: opts.onChange,
      });
    }
  };
  const onKeyTrigger = (e) => {
    if (e.key === 'Enter' || e.key === ' ' || e.key === 'ArrowDown') {
      e.preventDefault();
      onClick(e);
    }
  };

  trigger.addEventListener('click', onClick);
  trigger.addEventListener('keydown', onKeyTrigger);

  return () => {
    trigger.removeEventListener('click', onClick);
    trigger.removeEventListener('keydown', onKeyTrigger);
  };
}
